import { motion, AnimatePresence } from "framer-motion";
import { X, Lightning } from "@phosphor-icons/react";
import { IGamificationStats } from "../utils/queryOptions";
import { getLevelFromXp } from "../utils/gamificationHelpers";

export default function LevelDetailModal({
  isOpen,
  onClose,
  stats,
}: {
  isOpen: boolean;
  onClose: () => void;
  stats: IGamificationStats;
}) {
  const level = stats.level;
  const progress = stats.levelProgress;
  const levelInfo = getLevelFromXp(stats.totalXp);
  const currentLevelStartXp = stats.totalXp - levelInfo.currentLevelXp;
  const nextLevelXp = currentLevelStartXp + levelInfo.totalForNextLevel;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-surface-1 border border-white/[0.08] rounded-2xl p-6 max-w-sm w-full relative"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Current level */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-myPurple/15 rounded-2xl mb-3">
                <Lightning size={32} weight="fill" className="text-myPurple" />
              </div>
              <h3 className="text-2xl font-bold">Seviye {level}</h3>
              <p className="text-white/50 text-sm mt-1">{stats.totalXp} toplam XP</p>
            </div>

            {/* Progress to next level with thresholds */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-white/40 mb-1.5">
                <span>Seviye {level}</span>
                <span>Seviye {level + 1}</span>
              </div>
              <div className="relative">
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-myPurple to-myBlue rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                </div>
                {/* Threshold markers */}
                <div className="flex justify-between mt-1.5">
                  <span className="text-[10px] font-semibold text-myPurple">{currentLevelStartXp} XP</span>
                  <span className="text-[10px] font-bold text-white/60">{stats.totalXp} XP</span>
                  <span className="text-[10px] font-semibold text-white/30">{nextLevelXp} XP</span>
                </div>
              </div>
              <p className="text-xs text-white/40 mt-2 text-center">
                Sonraki seviyeye <span className="text-white/60 font-semibold">{stats.xpToNextLevel} XP</span> kaldı
              </p>
            </div>

            {/* XP breakdown info */}
            <div className="bg-white/5 rounded-xl p-4 space-y-2.5">
              <h4 className="text-sm font-semibold text-white/70 mb-3">XP Kazanımları</h4>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Test sorusu (doğru)</span>
                <span className="text-myGreen font-bold">+5 XP</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Kod sorusu (doğru)</span>
                <span className="text-myGreen font-bold">+10-20 XP</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Python sorusu (doğru)</span>
                <span className="text-myGreen font-bold">+15 XP</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">React sorusu (doğru)</span>
                <span className="text-myGreen font-bold">+20 XP</span>
              </div>
              <div className="border-t border-white/10 pt-2.5 mt-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Yanlış cevap</span>
                  <span className="text-myBlue font-bold">+1-4 XP</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
